import { Avatar, Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Group } from "../../../../types";
import { colors } from "../../../../theme/palette";
import { InviteIcon, RemoveIcon } from "../../../../assets/icons/MUI/EventsIcons";
import { useCreateEventContext } from "../cxt";
import { useGroupDataFormatter } from "../../../../hooks/useGroupDataFormatter";

const cardStyle = {
  display: "flex",
  flexDirection: "column",
  rowGap: "14px",
  padding: "12px",
  position: "relative",
  borderRadius: "4px",
  background: colors.White,
  boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
  height: "250px",
  width: "170px",
};

const groupNameStyle = {
  textDecoration: "none",
  color: colors.dark1000,
  textAlign: "center",
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: "normal",
  maxWidth: "80%",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  cursor: "pointer",
};

type GroupCardProps = {
  group: Group;
  maxInvitations?: number;
  currentSelectedCount?: number;
  onInviteLimitReached?: () => void;
};

const GroupCard = ({ group, maxInvitations, currentSelectedCount = 0, onInviteLimitReached }: GroupCardProps) => {
  const { name, firstTwoChr, id, media } = useGroupDataFormatter(group);

  const { state, setState } = useCreateEventContext();

  const selectedGroups = state.selectedGroups;
  const isInvited = selectedGroups.some((group) => group.id === id);

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  return (
    <Box sx={cardStyle}>
      <Avatar
        sx={{
          width: "72px",
          height: "72px",
          margin: "0 auto 9px",
        }}
        src={media}
      >
        {firstTwoChr}
      </Avatar>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "3px",
            width: "100%",
            justifyContent: "center",
            marginBottom: "5.5px",
          }}
        >
          <Typography sx={groupNameStyle}>{name}</Typography>
        </Box>
      </Box>

      {isInvited ? (
        <Button
          variant="outlined-sm"
          fullWidth
          startIcon={currentLanguage === "en" && <RemoveIcon />}
          endIcon={currentLanguage === "ar" && <RemoveIcon />}
          onClick={() =>
            setState((prevState) => ({
              ...prevState,
              selectedGroups: prevState.selectedGroups.filter((entity: any) => entity.id !== id),
            }))
          }
          sx={{
            marginTop: "auto",
            color: `${colors.Red200} !important`,
            borderColor: `${colors.Red200} !important`,
            background: "transparent !important",
          }}
        >
          {t("pages.events.createEvent.remove")}
        </Button>
      ) : (
        <Button
          variant="secondary-sm"
          fullWidth
          startIcon={currentLanguage === "en" && <InviteIcon />}
          endIcon={currentLanguage === "ar" && <InviteIcon />}
          sx={{
            marginTop: "auto",
          }}
          onClick={() => {
            // Check if adding this group would exceed the limit (only if limit exists)
            if (maxInvitations !== undefined && currentSelectedCount >= maxInvitations) {
              onInviteLimitReached?.();
              return;
            }
            setState((prevState) => ({
              ...prevState,
              selectedGroups: [...prevState.selectedGroups, group],
            }));
          }}
        >
          {t("pages.events.createEvent.invite")}
        </Button>
      )}
    </Box>
  );
};

export default GroupCard;
